// Basic
import { useState } from 'react';
import { useQueries } from 'react-query';
import PropTypes from 'prop-types';
// Libs
import { cn } from '../../../../../../../common-utils/libraries/tailwind/utils';
// Store & actions
import actionsUsers from '../../../api/actionsUsers';
import queryGetProfiles from '../../../api/queries/getProfiles';
import queryGetUser from '../../../api/queries/getUser';
import queryGetMerchantProfiles from '@/app/modules/users/merchant/api/queries/getMerchantProfiles';
// Components
import HeaderTitle from '../../../../../../../common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterForm from '../../../../../../../common-utils/components/renders/modal/components/footers/FooterForm/FooterForm';
import Select from '@/common-utils/components/ui/select';
import Entity from '@/common-utils/components/renders/entity/Entity';
import Switch from '@/common-utils/components/ui/switch';
import ScrollArea from '@/common-utils/components/ui/scrollArea';
import { Modal, ModalContent } from '../../../../../../../common-utils/components/ui/modal';
import { Button } from '@/common-utils/components/ui/button';
// Common
import UserPermissions from '../../permissions/UserPermissions';
import merchantUserFields from '@/app/modules/users/merchant/fields/merchantUserFields';
import userFields from '../../../fields/userFields';
import useUserPermissions from '../../hooks/useUserPermissions';
import ICONS from '@/common-utils/enums/icons';
import { useToast } from '@/common-utils/hooks/use-toast';
// Lang
import { nsUser } from '@/app/i18n/namespaces';
// Style
import './ModalPermissions.scss';

/**
 *
 * @param props
 * @param props.isOpen
 * @param props.setIsOpen
 * @param props.userId
 * @param props.organizationId
 * @param props.setLaunchRefetch
 * @param props.isMerchantUser
 * @returns ModalPermissions component
 */
function ModalPermissions({ isOpen, setIsOpen, userId, organizationId, setLaunchRefetch, isMerchantUser = false }) {

    // Toast
    const { toast } = useToast();

    // State
    const [profiles, setProfiles] = useState(undefined);
    const [profilesNames, setProfilesNames] = useState(undefined);
    const [allPermissionsToDisplay, setAllPermissionsToDisplay] = useState([]);
    const [userProfile, setUserProfile] = useState('');
    const [defaultUserProfile, setDefaultUserProfile] = useState('');
    const [userPermissions, setUserPermissions] = useState([]);
    const [defaultUserPermissions, setDefaultUserPermissions] = useState([]);
    const [showAllPermissions, setShowAllPermissions] = useState(false);
    const [disableResetToDefaultPermissions, setDisableResetToDefaultPermissions] = useState(false);

    // API Calls
    // --- Fetchers
    const [getProfilesResponse, getMerchantProfilesResponse, getUserResponse] = useQueries([
        queryGetProfiles(!isMerchantUser ? organizationId : null),
        queryGetMerchantProfiles(isMerchantUser ? organizationId : null),
        queryGetUser(userId)
    ]);

    // --- Mutators
    const onSuccess = () => {
        setLaunchRefetch(true);
        setIsOpen(false);
        toast({
            description: <Entity namespace={nsUser} entity='modal.permissions_update.success' />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity namespace={nsUser} entity='modal.permissions_update.error' />,
            variant: 'danger'
        });
    };
    const submitUpdatePermissions = actionsUsers.updatePermissions(onSuccess, onError);

    // Hooks
    useUserPermissions(isMerchantUser ? getMerchantProfilesResponse : getProfilesResponse, getUserResponse, userPermissions, userProfile, profiles, {
        setAllPermissionsToDisplay,
        setProfiles,
        setProfilesNames,
        setUserPermissions,
        setDefaultUserPermissions,
        setUserProfile,
        setDefaultUserProfile,
        setDisableResetToDefaultPermissions
    });

    // Other functions
    const setPermissionsFromProfile = (profileRequired) => {
        // Retrieve and set all permissions for the selected profile
        setUserPermissions(profiles[profileRequired].permissions);
    };

    return (
        <Modal open={isOpen} onOpenChange={setIsOpen} >
            <ModalContent className={cn('max-w-6xl')} data-cy='modal-update-permissions'>
                <HeaderTitle title='modal.permissions_update.title' namespace={nsUser} />
                <div className='modal-body-permissions'>
                    <div className='pre-body-fixed'>
                        <Entity className='modal-permission-label' entity='modal.permissions_update.profile' namespace={nsUser} />
                        <div className='select-profile-container'>
                            <Select
                                options={profilesNames}
                                prefixEntity={isMerchantUser ? merchantUserFields.MERCHANT_PROFILE.prefix : userFields.PROFILE.prefix}
                                namespace={isMerchantUser ? merchantUserFields.MERCHANT_PROFILE.namespace : userFields.PROFILE.namespace}
                                value={userProfile}
                                onChange={(value) => {
                                    // Update user's profile
                                    setUserProfile(value);
                                    // Set user's permissions based on selected profile
                                    setPermissionsFromProfile(value);
                                }}
                                dataCy='select-roles'
                            />
                            <div className='flex items-center'>
                                <Button
                                    onClick={() => {
                                        // Set user's permissions based on his current profile if checked
                                        setPermissionsFromProfile(userProfile);
                                    }}
                                    icon={ICONS.RESET}
                                    disabled={disableResetToDefaultPermissions}
                                    data-cy='button-reset-profile-permissions'
                                />
                                <Entity
                                    entity={'modal.permissions_update.reset'}
                                    namespace={nsUser}
                                    className={'flex-1 ml-4'}
                                />
                            </div>

                        </div>
                        <Entity
                            className='modal-permission-label'
                            entity='modal.permissions_update.permissions'
                            namespace={nsUser}
                            data={{ number: allPermissionsToDisplay.length - 1 }}
                        />
                        <div className='permissions-title-container'>
                            <Switch
                                name={'set_default_permission'}
                                checked={showAllPermissions}
                                onCheckedChange={() => {
                                    setShowAllPermissions(!showAllPermissions);
                                }}
                                entity={'modal.permissions_update.show_all'}
                                namespace={nsUser}
                                data-cy='switch-show-all-permissions'
                            />
                        </div>
                    </div>
                    <ScrollArea className='h-[50vh] w-full border border-gray-300 rounded'>
                        <div className='mx-4'>
                            {
                                userPermissions && allPermissionsToDisplay &&
                                <UserPermissions
                                    isUpdate={true}
                                    userPermissions={userPermissions}
                                    setUserPermissions={setUserPermissions}
                                    showAllPermissions={showAllPermissions}
                                    allPermissionsToDisplay={allPermissionsToDisplay}
                                />
                            }
                        </div>
                    </ScrollArea>
                </div>
                <FooterForm
                    onReset={() => {
                        setUserProfile(defaultUserProfile);
                        setUserPermissions(defaultUserPermissions);
                    }}
                    onSubmit={() => {
                        submitUpdatePermissions.mutate(
                            {
                                userId: userId,
                                profileUuid: profiles[userProfile].uuid,
                                permissions: userPermissions
                            }
                        );
                    }}
                    isSubmitDisabled={submitUpdatePermissions.isLoading}
                    isSubmitting={submitUpdatePermissions.isLoading}
                    showSubmitButton={true}
                />
            </ModalContent>;
        </Modal>
    );
}
ModalPermissions.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired,
    userId: PropTypes.string.isRequired,
    organizationId: PropTypes.string.isRequired,
    setLaunchRefetch: PropTypes.func.isRequired,
    isMerchantUser: PropTypes.bool
};

export default ModalPermissions;